import React, { useState, useMemo } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Footer, Button, ErrorBanner
} from './reModalShared';

import { DateField } from '../shared/DateField';

interface PositionLite {
  id: number;
  propertyName: string;
  propertyType: string;
  location: string;
  equityInvested: number;
  currentValuation: number;
}

interface Props {
  position: PositionLite;
  onClose: () => void;
  onSuccess: () => void;
}

const RealEstateDisposeModal: React.FC<Props> = ({ position, onClose, onSuccess }) => {
  const [saleDate, setSaleDate] = useState<string>(getTodayDate());
  const [salePrice, setSalePrice] = useState<string>(String(position.currentValuation || ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show estimated gain/loss vs equity invested so the PM has context.
  const realisedGain = useMemo(() => {
    const sp = Number(salePrice) || 0;
    return sp - (position.equityInvested || 0);
  }, [salePrice, position.equityInvested]);

  const submit = async () => {
    setError(null);
    if (!salePrice || Number(salePrice) <= 0) {
      setError('Sale price must be greater than 0'); return;
    }
    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-equity/${position.id}/dispose`, {
        saleDate,
        salePrice: Number(salePrice)
      });
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to record disposition';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Disposition"
      subtitle={`${position.propertyName} • ${position.propertyType} • ${position.location}`}
      icon={<AlertTriangle size={18} className="text-red-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(239, 68, 68, 0.06)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#fca5a5'
        }}>
          This will mark the property <b>SOLD</b>, emit a DISPOSE trade through the posting engine,
          and reduce <code>fact_positions</code> to zero. Cannot be undone via UI.
        </div>

        <FieldRow>
          <Field>
            <Label>Sale Date</Label>
            <DateField value={saleDate ?? ''} onChange={iso => setSaleDate(iso)} />
          </Field>
          <Field>
            <Label>Sale Price (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={salePrice}
              onChange={e => setSalePrice(e.target.value)}
            />
          </Field>
        </FieldRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Equity Invested</Label>
            <div style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 600 }}>
              ${(position.equityInvested || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </Field>
          <Field>
            <Label>Last Valuation</Label>
            <div style={{ color: '#60a5fa', fontSize: '0.95rem', fontWeight: 600 }}>
              ${(position.currentValuation || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </Field>
          <Field>
            <Label>Realised Gain / (Loss)</Label>
            <div style={{
              color: realisedGain >= 0 ? '#34d399' : '#f87171',
              fontSize: '0.95rem',
              fontWeight: 700
            }}>
              {realisedGain >= 0 ? '+' : ''}${realisedGain.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>

      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $danger onClick={submit} disabled={submitting}>
          {submitting ? 'Disposing…' : 'Confirm Disposition'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

export default RealEstateDisposeModal;
